import { useGraphqlQuery } from "graphql-mock"
import { IconCertified, useWindowWidth } from "materia"
import { useTranslation } from "react-i18next"
import { toPercent } from "utils"
import { Stars } from "../../../components/Stars"
import { UserProgress } from "../../../components/UserProgress"
import type { Count } from "../../../customApi"
import { query } from "./gql"
import { UserInfoMobileSkeleton } from "./UserInfoSkeleton"

import s from "./UserInfo.module.scss"

export const UserInfoConnected = () => {
  const { data, error, isPending } = useGraphqlQuery(query, {})

  if (error) return null

  const isLoading = isPending
  const { userProgressSummary: progress } = data ?? {}

  const userStars = {
    totalCount: progress?.productStarsCount,
    totalMax: progress?.productStarsMax,
  }

  const userProducts = {
    totalCount: progress?.scenarioCompleteCount,
    totalMax: progress?.scenarioCompleteMax,
  }

  return (
    <UserInfo
      userStars={userStars}
      userProducts={userProducts}
      isLoading={isLoading}
      error={error}
    />
  )
}

type UserInfoProps = {
  isLoading: boolean
  error: unknown
  userStars: Count
  userProducts: Count
}
export const UserInfo = ({
  isLoading,
  error,
  userStars,
  userProducts,
}: UserInfoProps) => {
  const { isBigWindow } = useWindowWidth()

  if (isLoading && !isBigWindow) return <UserInfoMobileSkeleton />
  if (error) return null

  if (!isBigWindow)
    return (
      <UserInfoMobile
        isLoading={isLoading}
        userStars={userStars}
        userProducts={userProducts}
        error={error}
      />
    )

  return (
    <div className={s.userInfo}>
      <UserProgress
        isLoading={isLoading}
        isMandatory={false}
        userStars={userStars}
        userProducts={userProducts}
      />
    </div>
  )
}

export const UserInfoMobile = ({
  userStars: { totalCount: productStarsCount, totalMax: productStarsMax },
  userProducts: {
    totalCount: scenarioCompleteCount,
    totalMax: scenarioCompleteMax,
  },
}: UserInfoProps) => {
  const { t } = useTranslation()

  return (
    <div className={s.userInfo}>
      <Completed
        label={t("user.COMPLETED")}
        count={scenarioCompleteCount}
        maxCount={scenarioCompleteMax}
      />
      <div className={s.userInfo__stars}>
        <Stars
          count={1}
          max={1}
          size="extraSmall"
          ariaLabel={`${productStarsCount} of ${productStarsMax}`}
        />
        <div className={s.userInfo__value}>
          {productStarsCount}/{productStarsMax}
        </div>
      </div>
    </div>
  )
}

type CompletedTypes = {
  count: number | undefined
  maxCount: number | undefined
  label: string
}
const Completed = ({ count, maxCount, label }: CompletedTypes) => {
  return (
    <div className={s.completed}>
      <IconCertified />
      <div className={s.completed__value}>
        {toPercent(Number(count), Number(maxCount))}%
      </div>
      <div className={s.completed__label}>{label}</div>
    </div>
  )
}
